import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Eye, Upload, RotateCcw, Languages, Rocket, Save, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, can } from "@/lib/auth";
import { siteImg } from "@/lib/site";
import { fmtDate } from "@/lib/format";
import { dict } from "@/lib/dict";
import { useFocusTarget } from "@/lib/focus";
import { Link } from "react-router-dom";
import { PageTitle, Loader, Section, Field, Empty } from "@/components/xd/ui";
import { useAct, ActionDialog } from "./Admin";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

const DEFAULTS = {
  hero_eyebrow: "home.eyebrow", hero_title: "home.title", hero_subtitle: null, hero_cta: "home.cta", hero_cta2: "nav.sold",
  categories_title: "home.categories", featured_eyebrow: "home.featured_eyebrow", featured_title: "home.featured",
  value1_t: "home.value1_t", value1_d: "home.value1_d", value2_t: "home.value2_t", value2_d: "home.value2_d", value3_t: "home.value3_t", value3_d: "home.value3_d",
  contact_title: "home.contact_title", contact_button: "vehicle.whatsapp", footer_tagline: "footer.tagline",
};
const GROUPS = [
  ["hero", ["hero_eyebrow", "hero_title", "hero_subtitle", "hero_cta", "hero_cta2"]],
  ["categories", ["categories_title"]],
  ["featured", ["featured_eyebrow", "featured_title"]],
  ["values", ["value1_t", "value1_d", "value2_t", "value2_d", "value3_t", "value3_d"]],
  ["contact", ["contact_title", "contact_button"]],
  ["footer", ["footer_tagline"]],
];
const LONG = ["hero_subtitle", "value1_d", "value2_d", "value3_d", "footer_tagline"];

const TextRow = ({ k, draft, setText }) => {
  const { t } = useLang();
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {["he", "ar"].map((l) => {
        const C = LONG.includes(k) ? Textarea : Input;
        return (
          <Field key={l} label={`${t(`site.key.${k}`)} · ${l === "he" ? "עברית" : "العربية"}`}>
            <C rows={LONG.includes(k) ? 3 : undefined} className="xd-input" placeholder={DEFAULTS[k] ? dict[l][DEFAULTS[k]] || "" : ""}
              value={draft.texts?.[k]?.[l] || ""} onChange={(e) => setText(k, l, e.target.value)} data-testid={`site-text-${k}-${l}`} />
          </Field>
        );
      })}
    </div>
  );
};

const HeroPreview = ({ hero, mobile }) => {
  const p = mobile ? hero.pos_mobile : hero.pos_desktop;
  return (
    <div className={`relative overflow-hidden border border-white/10 bg-[#111] ${mobile ? "w-28 aspect-square" : "w-full max-w-md aspect-video"}`} data-testid={`site-hero-preview-${mobile ? "mobile" : "desktop"}`}>
      <img src={hero.image_id ? siteImg(hero.image_id) : "/hero.jpg"} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${p[0]}% ${p[1]}%` }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-black" style={{ opacity: (hero.overlay / 100) * 0.6 }} />
      <div className="absolute bottom-2 start-2 end-2 space-y-1"><div className="h-1.5 w-2/3 bg-white/80" /><div className="h-1 w-1/2 bg-white/40" /><div className="h-2.5 w-10 bg-[var(--xd-red)] mt-1" /></div>
    </div>
  );
};

const PosSliders = ({ label, value, onChange, testid }) => (
  <div className="space-y-2">
    <p className="text-xs text-zinc-400">{label}</p>
    {[0, 1].map((i) => (
      <div key={i} className="flex items-center gap-3"><span className="w-6 text-xs text-zinc-500 ltr-num">{i ? "Y" : "X"}</span>
        <Slider dir="ltr" min={0} max={100} step={1} value={[value[i]]} onValueChange={([x]) => onChange(i ? [value[0], x] : [x, value[1]])} data-testid={`${testid}-${i ? "y" : "x"}`} />
        <span className="w-10 text-xs ltr-num">{value[i]}%</span></div>
    ))}
  </div>
);

const HeroEditor = ({ draft, set }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const h = draft.hero;
  const setHero = (patch) => set({ ...draft, hero: { ...h, ...patch } });
  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/admin/site/images", fd); setHero({ image_id: data.id }); toast.success(t("site.image_uploaded")); } catch { toast.error(t("err.unsupported_type")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="flex gap-3 items-end"><HeroPreview hero={h} /><HeroPreview hero={h} mobile /></div>
      <div className="flex flex-wrap gap-3">
        <label className={`btn-ghost cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <input type="file" accept="image/jpeg,image/png,image/heic,.heic" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} data-testid="site-hero-upload-input" />
          <Upload size={15} />{busy ? t("photos.uploading") : t("site.replace_image")}
        </label>
        {h.image_id && <button type="button" className="btn-ghost" onClick={() => setHero({ image_id: null })} data-testid="site-hero-default-image"><ImageOff size={15} />{t("site.default_image")}</button>}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="space-y-2"><p className="text-xs text-zinc-400">{t("site.overlay")}</p>
          <div className="flex items-center gap-3"><Slider dir="ltr" min={0} max={90} step={1} value={[h.overlay]} onValueChange={([x]) => setHero({ overlay: x })} data-testid="site-hero-overlay" /><span className="w-10 text-xs ltr-num">{h.overlay}%</span></div></div>
        <PosSliders label={t("site.focus_desktop")} value={h.pos_desktop} onChange={(v) => setHero({ pos_desktop: v })} testid="site-pos-desktop" />
        <PosSliders label={t("site.focus_mobile")} value={h.pos_mobile} onChange={(v) => setHero({ pos_mobile: v })} testid="site-pos-mobile" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label={t("site.link_cta")} hint={t("site.link_hint")}><Input dir="ltr" className="xd-input" value={draft.links.hero_cta} onChange={(e) => set({ ...draft, links: { ...draft.links, hero_cta: e.target.value } })} data-testid="site-link-hero_cta" /></Field>
        <Field label={t("site.link_cta2")}><Input dir="ltr" className="xd-input" value={draft.links.hero_cta2} onChange={(e) => set({ ...draft, links: { ...draft.links, hero_cta2: e.target.value } })} data-testid="site-link-hero_cta2" /></Field>
      </div>
      <label className="flex items-center gap-3 text-sm"><Switch checked={draft.show_cta2} onCheckedChange={(c) => set({ ...draft, show_cta2: c })} data-testid="site-show-cta2" />{t("site.show_cta2")}</label>
    </div>
  );
};

const SectionsEditor = ({ draft, set }) => {
  const { t } = useLang();
  const secs = draft.sections;
  const move = (i, d) => { const s = [...secs]; [s[i], s[i + d]] = [s[i + d], s[i]]; set({ ...draft, sections: s }); };
  return (
    <div className="space-y-2">
      <div className="xd-panel px-4 py-3 text-sm text-zinc-500">{t("site.sec.hero")} · {t("site.always_first")}</div>
      {secs.map((s, i) => (
        <div key={s.id} className="xd-panel px-4 py-3 flex items-center justify-between gap-3" data-testid={`site-section-${s.id}`}>
          <span className={`text-sm ${s.visible ? "text-white" : "text-zinc-600 line-through"}`}>{t(`site.sec.${s.id}`)}</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="h-9 w-9 grid place-items-center border border-white/10 disabled:opacity-30" data-testid={`site-section-up-${s.id}`} aria-label="up"><ArrowUp size={14} /></button>
            <button type="button" disabled={i === secs.length - 1} onClick={() => move(i, 1)} className="h-9 w-9 grid place-items-center border border-white/10 disabled:opacity-30" data-testid={`site-section-down-${s.id}`} aria-label="down"><ArrowDown size={14} /></button>
            <Switch checked={s.visible} onCheckedChange={(c) => set({ ...draft, sections: secs.map((x) => (x.id === s.id ? { ...x, visible: c } : x)) })} data-testid={`site-section-visible-${s.id}`} />
          </div>
        </div>
      ))}
    </div>
  );
};

const useTranslateAll = (draft, set) => {
  const act = useAct();
  return async () => {
    const fields = Object.fromEntries(Object.keys(DEFAULTS).filter((k) => draft.texts[k]?.he && !draft.texts[k]?.ar).map((k) => [k, draft.texts[k].he]));
    if (!Object.keys(fields).length) return toast.message("—");
    const r = await act(() => api.post("/admin/translate", { source: "he", fields }), false);
    if (r) set({ ...draft, texts: Object.fromEntries(Object.entries(draft.texts).map(([k, v]) => [k, { ...v, ar: v.ar || r.data.fields[k] || "" }])) });
  };
};

export default function AdminSite() {
  const { t } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const [d, setD] = useState(null);
  const [draft, setDraft] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/admin/site").then((r) => { setD(r.data); setDraft(r.data.draft); }).catch(() => setDenied(true));
  useEffect(() => { load(); }, []);
  const translateAll = useTranslateAll(draft, setDraft);
  useFocusTarget(!!draft);
  if (denied) return <Empty text={t("err.forbidden")} />;
  if (!draft) return <Loader />;
  const canEdit = can(user, "content.manage");
  const canPub = can(user, "content.publish");
  const dirty = JSON.stringify(draft) !== JSON.stringify(d.draft);
  const live = JSON.stringify(d.draft) === JSON.stringify(d.published);
  const setText = (k, l, v) => setDraft({ ...draft, texts: { ...draft.texts, [k]: { ...(draft.texts[k] || {}), [l]: v } } });
  const save = async () => { const r = await act(() => api.put("/admin/site/draft", { draft })); if (r) await load(); return r; };
  const preview = async () => { if (dirty && canEdit && !(await save())) return; window.open("/?preview=draft", "_blank"); };
  const publish = async () => { if (dirty && !(await save())) return; if (await act(() => api.post("/admin/site/publish"))) load(); };
  const restore = async () => { if (await act(() => api.post("/admin/site/restore"))) load(); };
  return (
    <div className="space-y-6" data-testid="site-editor">
      <Link to="/admin/site" className="text-xs text-zinc-400 hover:text-white" data-testid="site-home-back">→ {t("se.back")}</Link>
      <PageTitle eyebrow={t("admin.content")} title={t("site.title")}>
        <div className="flex flex-wrap gap-2">
          {canEdit && <button className="btn-ghost" disabled={!dirty} onClick={save} data-testid="site-save-draft-button"><Save size={15} />{t("site.save_draft")}</button>}
          <button className="btn-ghost" onClick={preview} data-testid="site-preview-button"><Eye size={15} />{t("site.preview")}</button>
          {canPub && <ActionDialog testid="site-publish" title={t("site.publish_confirm")} onSubmit={publish} trigger={<button className="btn-xd" data-testid="site-publish-button"><Rocket size={15} />{t("site.publish")}</button>} />}
          {canPub && d.has_previous && <ActionDialog testid="site-restore" title={t("site.restore_confirm")} onSubmit={restore} trigger={<button className="btn-ghost" data-testid="site-restore-button"><RotateCcw size={15} />{t("site.restore")}</button>} />}
        </div>
      </PageTitle>
      <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="site-status">
        <span className={`px-2 py-1 border ${dirty ? "border-amber-500 text-amber-300" : "border-white/10 text-zinc-400"}`}>{dirty ? t("site.unsaved") : t("site.saved_draft")}</span>
        <span className={`px-2 py-1 border ${live ? "border-emerald-700 text-emerald-300" : "border-amber-500 text-amber-300"}`}>{live ? t("site.live_matches") : t("site.live_differs")}</span>
        {d.updated_at && <span className="text-zinc-600 ltr-num">{fmtDate(d.updated_at)}</span>}
      </div>
      <p className="text-sm text-zinc-500">{t("site.intro")}</p>
      <fieldset disabled={!canEdit} className="space-y-6 min-w-0">
        <Section title={t("site.sec.hero")} testid="site-hero-section"><HeroEditor draft={draft} set={setDraft} /></Section>
        <Section title={t("site.sections")} testid="site-sections-section"><SectionsEditor draft={draft} set={setDraft} /></Section>
        <Section title={t("site.texts")} testid="site-texts-section" actions={canEdit && <button type="button" className="btn-ghost !min-h-[2.25rem] text-xs" onClick={translateAll} data-testid="site-translate-all"><Languages size={14} />{t("site.translate_missing")}</button>}>
          <p className="text-xs text-zinc-500">{t("site.texts_hint")}</p>
          {GROUPS.map(([g, keys]) => (
            <div key={g} className="space-y-3 border-t border-white/5 pt-4">
              <p className="eyebrow">{t(`site.sec.${g}`)}</p>
              {keys.map((k) => <TextRow key={k} k={k} draft={draft} setText={setText} />)}
            </div>
          ))}
        </Section>
      </fieldset>
    </div>
  );
}
